import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { PublicClubBrowseItem, clubsBookingApi } from '@/api/clubs-booking';
import PublicClubHeader from '@/components/public/PublicClubHeader';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { auth } from '@/store/authStore';
import { getClubSiteHref } from '@/utils/publicSite';

const PublicClubsBrowsePage = () => {
  const [items, setItems] = useState<PublicClubBrowseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await clubsBookingApi.listPublicClubs();
        setItems(response.items || []);
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load clubs');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => {
      return (
        item.clubName.toLowerCase().includes(needle) ||
        (item.description || '').toLowerCase().includes(needle)
      );
    });
  }, [items, search]);

  const isParentLoggedIn = (() => {
    const user = auth.getUser();
    return !!(user.isAuthenticated && user.role === 'parent');
  })();

  const content = (
    <div className="w-full px-3 pb-8 pt-6 sm:px-4 md:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Browse clubs</h1>
          <p className="mt-1 text-sm text-slate-600">Find clubs and explore their available activities.</p>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2.5">
            <Search className="h-5 w-5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by club name"
              className="w-full border-0 bg-transparent text-sm outline-none sm:text-base"
            />
          </div>
        </div>

        {loading && <p className="mt-4 text-sm text-slate-500">Loading clubs...</p>}
        {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        {!loading && !error && (
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((club) => (
              <article key={club.ownerId} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  {club.logoUrl ? (
                    <img src={club.logoUrl} alt={club.clubName} className="h-12" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F6FAF7] text-sm font-semibold text-[#1F8F5F]">
                      {(club.clubName || 'CB').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold text-slate-900">{club.clubName}</h2>
                    <p className="text-xs text-slate-500">{club.activityCount} activit{club.activityCount === 1 ? 'y' : 'ies'} live</p>
                  </div>
                </div>

                <p className="mt-3 line-clamp-3 text-sm text-slate-600">{club.description || 'No description provided.'}</p>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-500">
                    {club.nextDate ? `Next: ${new Date(club.nextDate).toLocaleDateString('en-GB')}` : 'No dates listed'}
                  </span>
                  <a
                    href={getClubSiteHref(club.publicSlug)}
                    className="rounded-lg border border-[#D8F1E2] bg-[#F3FBF7] px-3 py-1.5 text-sm font-medium text-[#1F8F5F] hover:bg-[#EAF7F0]"
                  >
                    View club
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
            No clubs found.
          </div>
        )}
      </div>
  );

  if (isParentLoggedIn) {
    return (
      <ParentPortalShell title="Browse clubs" subtitle="Find and book activities" showHeaderCard={false}>
        {content}
      </ParentPortalShell>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef0f4] text-slate-800">
      <PublicClubHeader
        clubName="Session Monkey"
        clubDescription="Discover clubs and book activities"
        preferSessionMonkeyFallbackLogo
        cartHref="/cart"
      />
      {content}
    </main>
  );
};

export default PublicClubsBrowsePage;
